import { forwardRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import straivLogo from '@/assets/straiv-logo-share.png';
import { ShareableWorkout } from '../ShareWorkoutCard';
import { RunningRouteVector, RouteCoordinate } from './RunningRouteVector';

interface ShareTemplateProps {
  workout: ShareableWorkout;
}

const formatTimeExact = (totalSeconds: number) => {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const secRounded = Math.round(s);
  const secStr = Math.abs(s - secRounded) < 0.01 ? String(secRounded).padStart(2, '0') : s.toFixed(1);
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${secStr}`;
  return `${m}:${secStr}`;
};

const formatPaceExact = (paceSeconds: number) => {
  if (!paceSeconds || !isFinite(paceSeconds) || paceSeconds <= 0) return '--:--';
  const m = Math.floor(paceSeconds / 60);
  const s = Math.round(paceSeconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
};

const formatTimeFromMinutes = (minutes: number) => {
  const h = Math.floor(minutes / 60);
  const m = Math.floor(minutes % 60);
  const s = Math.round((minutes % 1) * 60);
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
};

const formatShareTitle = (title: string) => {
  if (!title?.trim()) return title;
  const s = title
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .trim();
  return s.split(/\s+/).map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
};

/**
 * Static fallback for the Route share template. The PRIMARY flow on iOS
 * captures the live MapKit JS basemap via a native WKWebView screenshot of
 * `LiveRouteCapture` (see `ShareWorkoutModal.captureLiveRoute`). This static
 * version is used when the native plugin isn't available (web preview /
 * older iOS / failure recovery) so the share UI never ends up with nothing
 * to show. The polyline + START/FINISH flags sit on a dark gradient so it
 * still reads as a "route" share even without a real basemap.
 */
export const ShareTemplateRoute = forwardRef<HTMLDivElement, ShareTemplateProps>(
  ({ workout }, ref) => {
    const { t } = useTranslation();
    const routeCoordinates: RouteCoordinate[] = workout.route_coordinates || [];
    const distanceKm = workout.distance_km ?? 0;
    const paceSecondsPerKm = workout.pace_seconds_per_km;
    const durationSeconds = workout.duration_seconds;

    const validCoordinates = useMemo(
      () =>
        routeCoordinates.filter(
          c =>
            typeof c?.lat === 'number' &&
            typeof c?.lng === 'number' &&
            isFinite(c.lat) &&
            isFinite(c.lng)
        ),
      [routeCoordinates]
    );

    return (
      <div
        ref={ref}
        className="relative overflow-hidden"
        style={{
          width: '1080px',
          minWidth: '1080px',
          height: '1980px',
          background: 'radial-gradient(ellipse at center, #061613 0%, #020807 70%, #000000 100%)',
          padding: 0,
          boxSizing: 'border-box',
        }}
      >
        {/* Subtle grid texture so the dark canvas doesn't read as flat */}
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            opacity: 0.06,
            backgroundImage:
              'linear-gradient(rgba(10,255,194,0.7) 1px, transparent 1px), linear-gradient(90deg, rgba(10,255,194,0.7) 1px, transparent 1px)',
            backgroundSize: '120px 120px',
            pointerEvents: 'none',
          }}
        />

        {validCoordinates.length >= 2 && (
          <div
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: '320px',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <RunningRouteVector
              coordinates={validCoordinates}
              width={1000}
              height={1100}
              strokeColor="#0AFFC2"
              strokeWidth={9}
              showStartEnd={true}
            />
          </div>
        )}

        {/* Top: logo + title */}
        <div
          className="flex flex-col items-center"
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: '80px',
            padding: '0 80px',
          }}
        >
          <div
            style={{
              width: '320px',
              height: '92px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            <img
              src={straivLogo}
              alt="STRAIV"
              style={{ width: '320px', height: 'auto', display: 'block' }}
            />
          </div>
          <div
            className="font-bold text-center"
            style={{
              fontSize: '52px',
              color: '#0AFFC2',
              lineHeight: 1.1,
              marginTop: '16px',
              textShadow: '0 4px 24px rgba(0,0,0,0.7)',
            }}
          >
            {formatShareTitle(workout.title)}
          </div>
        </div>

        {/* Bottom: stats card + footer */}
        <div
          style={{
            position: 'absolute',
            left: '60px',
            right: '60px',
            bottom: '120px',
          }}
        >
          <div
            style={{
              background: 'linear-gradient(180deg, rgba(10,30,25,0.85) 0%, rgba(5,16,14,0.88) 100%)',
              border: '2px solid rgba(10, 255, 194, 0.25)',
              borderRadius: '36px',
              padding: '40px 48px',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              boxShadow: '0 24px 60px rgba(0,0,0,0.55)',
            }}
          >
            <div className="grid grid-cols-4 gap-6 text-center w-full">
              <div>
                <p className="uppercase tracking-wider" style={{ fontSize: '22px', color: 'rgba(255,255,255,0.6)', lineHeight: 1 }}>{t('components.shareTemplates.distance')}</p>
                <p className="font-bold" style={{ fontSize: '56px', color: '#0AFFC2', lineHeight: 1.15 }}>{distanceKm || '0'}</p>
                <p style={{ fontSize: '20px', color: 'rgba(255,255,255,0.5)' }}>km</p>
              </div>
              <div>
                <p className="uppercase tracking-wider" style={{ fontSize: '22px', color: 'rgba(255,255,255,0.6)', lineHeight: 1 }}>{t('components.shareTemplates.pace')}</p>
                <p className="font-bold" style={{ fontSize: '56px', color: '#0AFFC2', lineHeight: 1.15 }}>
                  {paceSecondsPerKm != null ? formatPaceExact(paceSecondsPerKm) : (workout.average_pace || '--:--')}
                </p>
                <p style={{ fontSize: '20px', color: 'rgba(255,255,255,0.5)' }}>/km</p>
              </div>
              <div>
                <p className="uppercase tracking-wider" style={{ fontSize: '22px', color: 'rgba(255,255,255,0.6)', lineHeight: 1 }}>{t('components.shareTemplates.time')}</p>
                <p className="font-bold" style={{ fontSize: '56px', color: '#0AFFC2', lineHeight: 1.15 }}>
                  {durationSeconds != null ? formatTimeExact(durationSeconds) : formatTimeFromMinutes(workout.duration_minutes)}
                </p>
                <p style={{ fontSize: '20px', color: 'rgba(255,255,255,0.5)' }}>&nbsp;</p>
              </div>
              <div>
                <p className="uppercase tracking-wider" style={{ fontSize: '22px', color: 'rgba(255,255,255,0.6)', lineHeight: 1 }}>{t('components.shareTemplates.calories')}</p>
                <p className="font-bold" style={{ fontSize: '56px', color: '#0AFFC2', lineHeight: 1.15 }}>{workout.calories_burned || '--'}</p>
                <p style={{ fontSize: '20px', color: 'rgba(255,255,255,0.5)' }}>kcal</p>
              </div>
            </div>
          </div>
          <p
            className="tracking-wide text-center"
            style={{
              fontSize: '24px',
              color: 'rgba(255,255,255,0.6)',
              marginTop: '28px',
              textShadow: '0 2px 8px rgba(0,0,0,0.7)',
            }}
          >
            {t('components.shareTemplatesExtra.poweredBy')}
          </p>
        </div>
      </div>
    );
  }
);

ShareTemplateRoute.displayName = 'ShareTemplateRoute';
